import React, { useState } from 'react';

const ChangePathDialog = ({ isOpen, onClose, onConfirm, currentPath, recommendedPath }) => {
  const [overrideReason, setOverrideReason] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [selectedPath, setSelectedPath] = useState(recommendedPath);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!overrideReason.trim() || !reviewDate) {
      alert('Please provide both a reason for override and a review date.');
      return;
    }

    onConfirm({
      path: selectedPath,
      reason: overrideReason,
      reviewDate: reviewDate
    });
    
    // Reset form
    setOverrideReason('');
    setReviewDate('');
    setSelectedPath(recommendedPath);
    onClose();
  };

  const handleCancel = () => {
    setOverrideReason('');
    setReviewDate('');
    setSelectedPath(recommendedPath);
    onClose();
  };

  if (!isOpen) return null;

  const getPathDisplayName = (path) => {
    if (path === 'NC_ONLY') return 'National Curriculum Only';
    if (path === 'NC_WITH_SUPPORTS') return 'National Curriculum with Supports';
    if (path === 'BESPOKE') return 'Bespoke Curriculum';
    return path;
  };

  const getPathColor = (path) => {
    if (path === 'NC_ONLY') return 'text-green-600';
    if (path === 'NC_WITH_SUPPORTS') return 'text-blue-600';
    if (path === 'BESPOKE') return 'text-purple-600';
    return 'text-gray-600';
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Override Path Recommendation</h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current and Recommended Paths */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Current Path:</span>
                  <span className={`text-sm font-semibold ${getPathColor(currentPath)}`}>
                    {getPathDisplayName(currentPath)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Recommended Path:</span>
                  <span className={`text-sm font-semibold ${getPathColor(recommendedPath)}`}>
                    {getPathDisplayName(recommendedPath)}
                  </span>
                </div>
              </div>
            </div>

            {/* Path Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Path *
              </label>
              <div className="space-y-2">
                {['NC_ONLY', 'NC_WITH_SUPPORTS', 'BESPOKE'].map((path) => (
                  <label key={path} className="flex items-center">
                    <input
                      type="radio"
                      name="path"
                      value={path}
                      checked={selectedPath === path}
                      onChange={(e) => setSelectedPath(e.target.value)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {getPathDisplayName(path)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Override Reason */}
            <div>
              <label htmlFor="overrideReason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Override *
              </label>
              <textarea
                id="overrideReason"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Please provide a detailed reason for overriding the recommended path..."
                required
              />
            </div>

            {/* Review Date */}
            <div>
              <label htmlFor="reviewDate" className="block text-sm font-medium text-gray-700 mb-2">
                Review Date *
              </label>
              <input
                type="date"
                id="reviewDate"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                This path will be reviewed on this date to assess its effectiveness.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Confirm Override
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePathDialog;
